import { useCallback, useEffect, useMemo, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { ClientError } from "graphql-request"
import { useGraphQLClient } from "./useGraphQLClient"
import {
  UpsertDbtDiaryFeatureFlagDocument,
  UpsertDbtDiaryFeatureFlagMutation,
  UpsertDbtDiaryFeatureFlagMutationVariables,
  UpsertViraPathFeatureFlagDocument,
  UpsertViraPathFeatureFlagMutation,
  UpsertViraPathFeatureFlagMutationVariables,
} from "../generated/graphql"

interface FeatureFlagSnapshot {
  dbtDiaryEnabled?: boolean
  viraPathEnabled?: boolean
}

interface ClientFeatureFlagController {
  enabled: boolean | null
  success: string | null
  error: string | null
  isUpserting: boolean
  resetFeedback: () => void
  setEnabled: (nextEnabled: boolean) => Promise<void>
}

/**
 * Manage client-specific feature flags.
 *
 * Returns grouped controllers (`dbtDiary`, `viraPath`) so additional flags can
 * be added without expanding the top-level return surface indefinitely.
 *
 * @param clientId - The client/user id whose feature flags are being updated.
 * @param featureFlags - Optional snapshot of initial feature flag values.
 */
function useClientFeatureFlags(
  clientId: string,
  featureFlags: FeatureFlagSnapshot = {}
) {
  const graphQLClient = useGraphQLClient()

  // -----------------
  // DBT Diary Feature
  // -----------------
  const [dbtDiaryEnabled, setDbtDiaryEnabled] = useState<boolean | null>(
    typeof featureFlags.dbtDiaryEnabled === "boolean"
      ? featureFlags.dbtDiaryEnabled
      : null
  )
  const [dbtDiarySuccess, setDbtDiarySuccess] = useState<string | null>(null)
  const [dbtDiaryError, setDbtDiaryError] = useState<string | null>(null)

  useEffect(() => {
    if (typeof featureFlags.dbtDiaryEnabled === "boolean") {
      setDbtDiaryEnabled(featureFlags.dbtDiaryEnabled)
    }
  }, [featureFlags.dbtDiaryEnabled])

  const {
    mutateAsync: mutateDbtDiary,
    isPending: isUpsertingDbtDiary,
    reset: resetDbtDiaryMutation,
  } = useMutation<UpsertDbtDiaryFeatureFlagMutation, Error, boolean>({
    mutationKey: ["UpsertDBTDiaryFeatureFlag", clientId],
    mutationFn: async (flagValue: boolean) => {
      if (!clientId) {
        throw new Error("Client id is required to update DBT diary flag")
      }

      try {
        return await graphQLClient.request<
          UpsertDbtDiaryFeatureFlagMutation,
          UpsertDbtDiaryFeatureFlagMutationVariables
        >(UpsertDbtDiaryFeatureFlagDocument, {
          _eq: clientId,
          dbt_diary_enabled: flagValue,
        })
      } catch (error) {
        let message = "Unable to update the DBT Diary feature flag."
        if (error instanceof ClientError) {
          message =
            error.response.errors?.[0]?.message ??
            `${message} Please try again.`
        } else if (error instanceof Error && error.message) {
          message = error.message
        }
        console.error("DBT Diary feature toggle failed", error)
        throw new Error(message)
      }
    },
  })

  const resetDbtDiaryFeedback = useCallback(() => {
    setDbtDiaryError(null)
    setDbtDiarySuccess(null)
    resetDbtDiaryMutation()
  }, [resetDbtDiaryMutation])

  const setDbtDiaryFeatureEnabled = useCallback(
    async (nextEnabled: boolean) => {
      setDbtDiaryError(null)
      setDbtDiarySuccess(null)
      try {
        await mutateDbtDiary(nextEnabled)
        setDbtDiaryEnabled(nextEnabled)
        setDbtDiarySuccess(
          `DBT Diary feature ${nextEnabled ? "enabled" : "disabled"}.`
        )
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Unable to update the DBT Diary feature. Please try again."
        setDbtDiaryError(message)
        throw error
      }
    },
    [mutateDbtDiary]
  )

  // -----------------
  // Vira Path Feature
  // -----------------
  const [viraPathEnabled, setViraPathEnabled] = useState<boolean | null>(
    typeof featureFlags.viraPathEnabled === "boolean"
      ? featureFlags.viraPathEnabled
      : null
  )
  const [viraPathSuccess, setViraPathSuccess] = useState<string | null>(null)
  const [viraPathError, setViraPathError] = useState<string | null>(null)

  useEffect(() => {
    if (typeof featureFlags.viraPathEnabled === "boolean") {
      setViraPathEnabled(featureFlags.viraPathEnabled)
    }
  }, [featureFlags.viraPathEnabled])

  const {
    mutateAsync: mutateViraPath,
    isPending: isUpsertingViraPath,
    reset: resetViraPathMutation,
  } = useMutation<UpsertViraPathFeatureFlagMutation, Error, boolean>({
    mutationKey: ["UpsertViraPathFeatureFlag", clientId],
    mutationFn: async (flagValue: boolean) => {
      if (!clientId) {
        throw new Error("Client id is required to update Vira Path flag")
      }

      try {
        return await graphQLClient.request<
          UpsertViraPathFeatureFlagMutation,
          UpsertViraPathFeatureFlagMutationVariables
        >(UpsertViraPathFeatureFlagDocument, {
          _eq: clientId,
          vira_path_enabled: flagValue,
        })
      } catch (error) {
        let message = "Unable to update the Vira Path feature flag."
        if (error instanceof ClientError) {
          message =
            error.response.errors?.[0]?.message ??
            `${message} Please try again.`
        } else if (error instanceof Error && error.message) {
          message = error.message
        }
        console.error("Vira Path feature toggle failed", error)
        throw new Error(message)
      }
    },
  })

  const resetViraPathFeedback = useCallback(() => {
    setViraPathError(null)
    setViraPathSuccess(null)
    resetViraPathMutation()
  }, [resetViraPathMutation])

  const setViraPathFeatureEnabled = useCallback(
    async (nextEnabled: boolean) => {
      setViraPathError(null)
      setViraPathSuccess(null)
      try {
        await mutateViraPath(nextEnabled)
        setViraPathEnabled(nextEnabled)
        setViraPathSuccess(
          `Vira Path feature ${nextEnabled ? "enabled" : "disabled"}.`
        )
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Unable to update the Vira Path feature. Please try again."
        setViraPathError(message)
        throw error
      }
    },
    [mutateViraPath]
  )

  const dbtDiary = useMemo<ClientFeatureFlagController>(
    () => ({
      enabled: dbtDiaryEnabled,
      success: dbtDiarySuccess,
      error: dbtDiaryError,
      isUpserting: isUpsertingDbtDiary,
      resetFeedback: resetDbtDiaryFeedback,
      setEnabled: setDbtDiaryFeatureEnabled,
    }),
    [
      dbtDiaryEnabled,
      dbtDiarySuccess,
      dbtDiaryError,
      isUpsertingDbtDiary,
      resetDbtDiaryFeedback,
      setDbtDiaryFeatureEnabled,
    ]
  )

  const viraPath = useMemo<ClientFeatureFlagController>(
    () => ({
      enabled: viraPathEnabled,
      success: viraPathSuccess,
      error: viraPathError,
      isUpserting: isUpsertingViraPath,
      resetFeedback: resetViraPathFeedback,
      setEnabled: setViraPathFeatureEnabled,
    }),
    [
      viraPathEnabled,
      viraPathSuccess,
      viraPathError,
      isUpsertingViraPath,
      resetViraPathFeedback,
      setViraPathFeatureEnabled,
    ]
  )

  return useMemo(
    () => ({
      dbtDiary,
      viraPath,
    }),
    [dbtDiary, viraPath]
  )
}

export default useClientFeatureFlags
